import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import DateSelector from "../../../components/shared/fields/DateSelector";
import LotSelector from "../../../components/shared/fields/LotSelector";
import GridCard from "../../../components/shared/gridCard/GridCard";
import InfoCard from "../../../components/shared/infocard/InfoCard";
import Button from "../../../components/shared/buttons/button";
import useTranslator from "../../../hooks/useTranslator";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import NoDataFound from "../../../components/shared/noDataFound/NoDataFound";
import Box from "../../../images/boxes.svg";
import moment from "moment";
import TotalKgBaggedModal from "./TotalKgBaggedModal";
import TreysCompletedModal from "./TreysCompletedModal";
import SeparationModal from "./SeparationModal";

import { 
  useGetBaggingLogsQuery, 
  useCreateBaggingDataMutation,
  useGetBaggedRecordQuery,
  useUpdateBaggedRecordMutation,
  useGetCompletedRecordQuery,
  useCreateCompletedRecordMutation,
  useUpdateCompletedRecordMutation,
  useGetSeparationRecordQuery,
  useCreateSeparationRecordMutation,
  useUpdateSeparationRecordMutation
} from "../packing/packingApi";
import { formatDateAndTime, getCurrentDate, makeLotNumber, makePriorLotNumber, makeShiftWiseLotNumber } from "../../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../../helpers/constants";

const Bagging = () => {
  const { _t } = useTranslator();
  
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makeShiftWiseLotNumber(date));
  
  
  // Modal states
  const [showBaggedModal, setShowBaggedModal] = useState(false);
  const [showTreysModal, setShowTreysModal] = useState(false);
  const [showDaySeparationModal, setShowDaySeparationModal] = useState(false);
  const [showNightSeparationModal, setShowNightSeparationModal] = useState(false);
  const [baggedMode, setBaggedMode] = useState("add");
  const [treysMode, setTreysMode] = useState("add");
  const [daySeparationMode, setDaySeparationMode] = useState("add");
  const [nightSeparationMode, setNightSeparationMode] = useState("add");

  // Data states
  const [baggedData, setBaggedData] = useState(null);
  const [treysData, setTreysData] = useState(null);
  const [daySeparationData, setDaySeparationData] = useState(null);
  const [nightSeparationData, setNightSeparationData] = useState(null);

  // API Query and Mutations
  const { data: baggingLogsData, isLoading } = useGetBaggingLogsQuery({ date:date,lot_number:lotNumber });
  const { data: baggedRecordData } = useGetBaggedRecordQuery({ date:date,lot_number:lotNumber });
  const { data: completedRecordData } = useGetCompletedRecordQuery({ date ,lot_number:lotNumber });
  // const { data: daySeparationRecordData } = useGetSeparationRecordQuery({ date, shift: "day", separation_area: "bagging" });
  const { data: nightSeparationRecordData } = useGetSeparationRecordQuery({ date, shift: "night", separation_area: "bagging" ,lot_number:lotNumber});
  const [createBaggingData, { isLoading: isCreating }] = useCreateBaggingDataMutation();
  const [updateBaggedRecord, { isLoading: isUpdating }] = useUpdateBaggedRecordMutation();
  const [createCompletedRecord] = useCreateCompletedRecordMutation();
  const [updateCompletedRecord] = useUpdateCompletedRecordMutation();
  const [createSeparationRecord] = useCreateSeparationRecordMutation();
  const [updateSeparationRecord] = useUpdateSeparationRecordMutation();

  useEffect(() => {
    setLotNumber(makeShiftWiseLotNumber(date));
  }, [date]);

  useEffect(() => {
    // Update data from bagged record API
    if (baggedRecordData?.data) {
      setBaggedData(baggedRecordData.data);
    } else {
      setBaggedData(null);
    }
  }, [baggedRecordData]);

  useEffect(() => {
    // Update treys data from not completed record API
    if (completedRecordData?.data) {
      setTreysData(completedRecordData.data);
    } else {
      setTreysData(null);
    }
  }, [completedRecordData]);

  useEffect(() => {
    // Update night separation data
    if (nightSeparationRecordData?.data) {
      setNightSeparationData(nightSeparationRecordData.data);
    } else {
      setNightSeparationData(null);
    }
  }, [nightSeparationRecordData]);

  const handleBaggedAdd = () => {
    setShowBaggedModal(true);
    setBaggedMode(baggedData ? "edit" : "add");
  };

  const handleTreysAdd = () => {
    setShowTreysModal(true);
    setTreysMode(treysData ? "edit" : "add");
  };

  const handleBaggedSave = async (data) => {
    try {
      if (baggedData && baggedData.id) {
        // Update existing record
        await updateBaggedRecord({ 
          id: baggedData.id, 
          requestBody: data 
        }).unwrap();
      } else {
        // Create new record
        await createBaggingData(data).unwrap();
      }
    } catch (error) {
      console.error("Error saving bagged data:", error);
    }
  };

  const handleTreysSave = async (data) => {
    try {
      if (treysData && treysData.id) {
        // Update existing record
        await updateCompletedRecord({ 
          id: treysData.id, 
          requestBody: data 
        }).unwrap();
      } else {
        // Create new record
        await createCompletedRecord(data).unwrap();
      }
    } catch (error) {
      console.error("Error saving treys not completed data:", error);
    }
  };

  const handleDaySeparationAdd = () => {
    setShowDaySeparationModal(true);
    setDaySeparationMode(daySeparationData ? "edit" : "add");
  };

  const handleNightSeparationAdd = () => {
    setShowNightSeparationModal(true);
    setNightSeparationMode(nightSeparationData ? "edit" : "add");
  };

  const handleDaySeparationSave = async (data) => {
    try {
      if (daySeparationData && daySeparationData.id) {
        await updateSeparationRecord({ 
          id: daySeparationData.id, 
          requestBody: data 
        }).unwrap();
      } else {
        await createSeparationRecord(data).unwrap();
      }
    } catch (error) {
      console.error("Error saving day separation data:", error);
    }
  };

  const handleNightSeparationSave = async (data) => {
    try {
      if (nightSeparationData && nightSeparationData.id) {
        await updateSeparationRecord({ 
          id: nightSeparationData.id, 
          requestBody: data 
        }).unwrap();
      } else {
        await createSeparationRecord(data).unwrap();
      }
    } catch (error) {
      console.error("Error saving night separation data:", error);
    }
  };

  const baggedLogData = baggedData
    ? [
        [
          { label: _t("kg_cheeks", "KG Of Cheeks"), value: baggedData.cheeks_kg || 0 },
          { label: _t("kg_strips", "KG Of Strips"), value: baggedData.strips_kg || 0 },
          { label: _t("kg_tapita", "KG Of Tapita"), value: baggedData.tapita_kg || 0 },
          { label: _t("total_kg", "Total KG"), value: baggedData.total_kg || 0 }
        ],
      ]
    : [];

  const treysLogData = treysData
    ? [
        [
          { label: _t("total_cart_completed", "Total Cart Completed"), value: treysData.total_cart || 0 },
          { label: _t("cart_not_completed", "Cart Not Completed"), value: treysData.cart_not_completed || 0 },
          { label: _t("total_cheeks_tray", "Total Cheeks Tray"), value: treysData.total_cheeks_tray || 0 },
          { label: _t("total_strips_tray", "Total Strips Tray"), value: treysData.total_strips_tray || 0 },
          { label: _t("total_tapita_tray", "Total Tapita Tray"), value: treysData.total_tapita_tray || 0 },
        ],
      ]
    : [];

  const daySeparationLogData = daySeparationData
    ? [
        [
          { label: _t("strips_kg", "Strips KG"), value: daySeparationData.strips_kg || 0 },
          { label: _t("cheeks_kg", "Cheeks KG"), value: daySeparationData.cheeks_kg || 0 },
          { label: _t("number_of_people_separating", "Number of people Separating"), value: daySeparationData.total_people || 0 },
        ],
      ]
    : [];

  const nightSeparationLogData = nightSeparationData
    ? [
        [
          { label: _t("strips_kg", "Strips KG"), value: nightSeparationData.strips_kg || 0 },
          { label: _t("cheeks_kg", "Cheeks KG"), value: nightSeparationData.cheeks_kg || 0 },
          { label: _t("number_of_people_separating", "Number of people Separating"), value: nightSeparationData.total_people || 0 },
        ],
      ]
    : [];

  const listItems = [
    {
      label: _t("total_tray", "Total Tray"),
      value: baggingLogsData?.data?.total_tray || 0,
      icon: Box,
    },
    {
      label: _t("total_cheeks_tray", "Total Cheeks Tray"),
      value: baggingLogsData?.data?.total_cheeks_tray || 0,
      icon: Box,
    },
    {
      label: _t("total_tapita_tray", "Total Tapita Tray"),
      value: baggingLogsData?.data?.total_tapita_tray || 0,
      icon: Box,
    },
    {
      label: _t("total_strip_tray", "Total Strip Tray"),
      value: baggingLogsData?.data?.total_strip_tray || 0,
      icon: Box,
    },

    // Not completed Logs
    {
      label: _t("total_cart_completed", "Total Cart Completed"),
      value: baggingLogsData?.data?.total_cart_completed || 0,
      icon: Box,
    },
    {
      label: _t("cheeks_tray_completed", "Cheeks Tray Completed"),
      value: baggingLogsData?.data?.cheeks_tray_completed || 0,
      icon: Box,
    },
    {
      label: _t("tapita_tray_completed", "Tapita Tray Completed"),
      value: baggingLogsData?.data?.tapita_tray_completed || 0,
      icon: Box,
    },
    {
      label: _t("strips_tray_completed", "Strips Tray Completed"),
      value: baggingLogsData?.data?.strip_tray_completed || 0,
      icon: Box,
    },
  ];

  return (
    <div>
      <TotalKgBaggedModal
        show={showBaggedModal}
        setShow={setShowBaggedModal}
        record={baggedData}
        mode={baggedMode}
        onSave={handleBaggedSave}
        date={date}
        lotNumber={lotNumber}
      />
      
      <TreysCompletedModal
        show={showTreysModal}
        setShow={setShowTreysModal}
        record={treysData}
        mode={treysMode}
        onSave={handleTreysSave}
        date={date}
        lotNumber={lotNumber}
      />

      <SeparationModal
        show={showDaySeparationModal}
        setShow={setShowDaySeparationModal}
        record={daySeparationData}
        mode={daySeparationMode}
        onSave={handleDaySeparationSave}
        date={date}
        lotNumber={lotNumber}
        shift="day"
        separationArea="bagging"
      />

      <SeparationModal
        show={showNightSeparationModal}
        setShow={setShowNightSeparationModal}
        record={nightSeparationData}
        mode={nightSeparationMode}
        onSave={handleNightSeparationSave}
        date={date}
        lotNumber={lotNumber}
        shift="night"
        separationArea="bagging"
      />
      
      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">
            {_t("bagging_overview_title", "Bagging Overview")}
          </h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard
              key={index}
              title={list.label}
              value={list.value}
              icon={list.icon}
              day={currentDate === date ? _t("common_today", "Today") : formatDateAndTime(date ,PROJECT_DATE_FORMAT)}
            />
          ))}
        </ul>
      </div>

      <div className="my-3">
        <h6 className="mb-3">{_t("bagging_logs", "Bagging Logs")}</h6>
        <div className="gap-2 overviewcard">
          <Row>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">
                      {_t("total_kg_bagged", "KG Bagged")}
                    </h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no','Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {(date === currentDate || !baggedData) && (
                    <Button
                      size="xs"
                      label={baggedData ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                      icon={PlusSvg}
                      onClick={handleBaggedAdd}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {baggedLogData.length > 0 ? (
                      baggedLogData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item, itemIndex) => (
                            <Col lg={6} key={`${index}-${itemIndex}`}>
                              <InfoCard label={item.label} value={item.value} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">
                      {_t("trays_completed", "Trays Completed")}
                    </h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no','Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {(date === currentDate || !treysData) && (
                    <Button
                      size="xs"
                      label={treysData ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                      icon={PlusSvg}
                      onClick={handleTreysAdd}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {treysLogData.length > 0 ? (
                      treysLogData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item) => (
                            <Col lg={6} key={item.label}>
                              <InfoCard label={item.label} value={item.value} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col lg={6}>
              <div className="dash_card h-100">
                <div className="d-flex justify-content-between align-items-center dash_header">
                  <div className="packing-card-header-content">
                    <h3 className="mb-0">
                      {_t("night_separation", "Night Separation")}
                    </h3>
                    <span className="shift-badge packing-lot-badge">{`${_t('lot_no','Lot No.')} #${lotNumber}`}</span>
                  </div>
                  {(date === currentDate || !nightSeparationData) && (
                    <Button
                      size="xs"
                      label={nightSeparationData ? _t("edit_data", "Edit Data") : _t("add_data", "Add Data")}
                      icon={PlusSvg}
                      onClick={handleNightSeparationAdd}
                    />
                  )}
                </div>
                <div className="p-3">
                  <Row>
                    {nightSeparationLogData.length > 0 ? (
                      nightSeparationLogData.map((data, index) => (
                        <Row key={index}>
                          {data.map((item, itemIndex) => (
                            <Col lg={6} key={`${index}-${itemIndex}`}>
                              <InfoCard label={item.label} value={item.value} />
                            </Col>
                          ))}
                        </Row>
                      ))
                    ) : (
                      <NoDataFound />
                    )}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Bagging;
